import React, { useEffect, useState } from "react";
import { CalendarClock, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { formatDateOnly, todayInput } from "@/lib/date";

// Director's bio due date for the current list (team-wide or one subgroup).
export default function BioDeadlineControl({ deadline, today, groupName, subgroupId, viewOnly, onChange }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setValue(deadline?.due_date || ""); }, [open, deadline]);

  const params = subgroupId ? { subgroup_id: subgroupId } : {};
  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); setOpen(false); onChange(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const save = () => run(() => api.put("/bios/deadline", { due_date: value, ...params }), "Due date saved");
  const clear = () => run(() => api.delete("/bios/deadline", { params }), "Due date removed");

  const overdue = deadline && deadline.due_date < today;
  const label = deadline ? `${overdue ? "Past due" : "Due"} ${formatDateOnly(deadline.due_date)}` : "Set due date";
  const tone = overdue ? "border-red-200 bg-red-50 text-red-700 hover:bg-red-100" : deadline ? "border-sky-200 bg-sky-50 text-sky-800 hover:bg-sky-100" : "";

  if (viewOnly) return deadline ? <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${tone}`} data-testid="bios-deadline-badge"><CalendarClock className="h-3.5 w-3.5" /> {label}</span> : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className={`rounded-full ${tone}`} data-testid="bios-deadline-btn">
          <CalendarClock className="mr-1.5 h-3.5 w-3.5" /> {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72" data-testid="bios-deadline-popover">
        <p className="font-heading text-sm font-bold text-slate-900">Bio due date · {groupName || "Whole team"}</p>
        <p className="mt-1 text-xs text-slate-500">At 8:00 AM the day before, you'll get a summary of who's still missing a bio so you can nudge them. Members aren't contacted automatically.</p>
        <div className="mt-3 space-y-1.5">
          <Label className="text-xs">Due date</Label>
          <Input type="date" min={todayInput()} value={value} onChange={(e) => setValue(e.target.value)} data-testid="bios-deadline-input" />
        </div>
        <div className="mt-3 flex justify-between gap-2">
          {deadline ? <Button variant="ghost" size="sm" className="rounded-full text-red-600 hover:bg-red-50" disabled={busy} onClick={clear} data-testid="bios-deadline-clear">Remove</Button> : <span />}
          <Button size="sm" className="rounded-full bg-slate-900 hover:bg-slate-800" disabled={busy || !value} onClick={save} data-testid="bios-deadline-save">
            {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />} Save
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
